import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { SpinnerGap, ShoppingBag, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import Footer from "../components/Footer";
import UpiPaymentForm from "../components/UpiPaymentForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const [step, setStep] = useState("details");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [shipping, setShipping] = useState({
    address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "IN",
  });
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [order, setOrder] = useState(null);

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setCreateErr("");
    if (!customer.name.trim() || !customer.email.trim()) {
      setCreateErr("Please enter your name and email");
      return;
    }
    if (!shipping.address_line1.trim() || !shipping.city.trim() || !shipping.zip_code.trim()) {
      setCreateErr("Please enter your shipping address");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/orders/create`, {
        cart_items: items.map((i) => ({ id: i.id, quantity: i.quantity })),
        customer,
        shipping,
        notes,
      });
      setOrder(res.data);
      setStep("payment");
    } catch (err) {
      setCreateErr(err?.response?.data?.detail || "Failed to create order");
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitted = () => {
    clearCart();
    navigate(`/order/${order.order_id}`);
  };

  if (items.length === 0 && step === "details") {
    return (
      <div className="pt-20 md:pt-24 min-h-screen">
        <div className="max-w-[800px] mx-auto px-6 py-16 text-center">
          <ShoppingBag size={48} className="mx-auto text-[#E01A23] mb-6" weight="duotone" />
          <h1 className="text-3xl md:text-4xl text-[#1A1A1A] mb-3" style={{ fontFamily: "'Lazy Dog', cursive" }}>
            your cart is empty
          </h1>
          <p className="text-sm text-[#3a3a3a] mb-6" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
            add some art to your cart before checking out.
          </p>
          <Button onClick={() => navigate("/shop")} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-11 px-8 text-sm">
            go to shop
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
        <div className="flex items-center gap-2 mb-8 text-xs" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
          <span className={step === "details" ? "text-[#E01A23] font-bold" : "text-[#3a3a3a]"}>1. your details</span>
          <ArrowRight size={14} className="text-[#3a3a3a]" />
          <span className={step === "payment" ? "text-[#E01A23] font-bold" : "text-[#3a3a3a]"}>2. upi payment</span>
        </div>

        <h1 className="text-3xl md:text-5xl text-[#1A1A1A] mb-2" style={{ fontFamily: "'Lazy Dog', cursive" }}>checkout</h1>
        <p className="text-sm text-[#3a3a3a] mb-8" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
          we accept UPI payments. pay via any UPI app, share the UTR, and we'll confirm your order.
        </p>

        <div className="grid md:grid-cols-[1fr_360px] gap-8 md:gap-12">
          <div>
            {step === "details" && (
              <form onSubmit={handleCreateOrder} className="space-y-5" data-testid="checkout-details-form">
                <h2 className="text-xl text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>your details</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="checkout-name" placeholder="full name *" value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                  <Input data-testid="checkout-email" type="email" placeholder="email *" value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                </div>
                <Input data-testid="checkout-phone" placeholder="phone (optional, helps with delivery)" value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />

                <h2 className="text-xl text-[#1A1A1A] pt-4" style={{ fontFamily: "'Lazy Dog', cursive" }}>shipping address</h2>
                <Input data-testid="checkout-addr1" placeholder="address line 1 *" value={shipping.address_line1} onChange={(e) => setShipping((s) => ({ ...s, address_line1: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                <Input data-testid="checkout-addr2" placeholder="apt, suite, etc. (optional)" value={shipping.address_line2} onChange={(e) => setShipping((s) => ({ ...s, address_line2: e.target.value }))} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Input data-testid="checkout-city" placeholder="city *" value={shipping.city} onChange={(e) => setShipping((s) => ({ ...s, city: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                  <Input data-testid="checkout-state" placeholder="state *" value={shipping.state} onChange={(e) => setShipping((s) => ({ ...s, state: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                  <Input data-testid="checkout-zip" placeholder="zip / pin *" value={shipping.zip_code} onChange={(e) => setShipping((s) => ({ ...s, zip_code: e.target.value }))} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                  <Input data-testid="checkout-country" placeholder="country" value={shipping.country} onChange={(e) => setShipping((s) => ({ ...s, country: e.target.value }))} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                </div>

                <Textarea data-testid="checkout-notes" placeholder="order notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" rows={3} />

                {createErr && <p className="text-sm text-[#E01A23]" data-testid="checkout-error">{createErr}</p>}

                <Button data-testid="checkout-continue-btn" type="submit" disabled={creating} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-11 px-8 text-sm">
                  {creating ? <><SpinnerGap size={16} className="animate-spin mr-2" /> creating order…</> : <>continue to payment <ArrowRight size={16} className="ml-2" /></>}
                </Button>
              </form>
            )}

            {step === "payment" && order && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setStep("details")}
                  className="flex items-center gap-2 text-xs text-[#3a3a3a] hover:text-[#E01A23]"
                  style={{ fontFamily: "'Courier New', Courier, monospace" }}
                >
                  <ArrowLeft size={14} /> back to details
                </button>
                <UpiPaymentForm order={order} onSubmitted={handleSubmitted} />
              </div>
            )}
          </div>

          <aside className="md:sticky md:top-24 h-fit bg-[#EBD988] rounded-2xl p-5 md:p-6">
            <h3 className="text-lg text-[#1A1A1A] mb-4" style={{ fontFamily: "'Lazy Dog', cursive" }}>order summary</h3>
            <div className="space-y-3 mb-4">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-3" data-testid={`summary-item-${it.id}`}>
                  <img src={it.image} alt={it.name} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#1A1A1A] truncate" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{it.name}</p>
                    <p className="text-[10px] text-[#555]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>qty {it.quantity}</p>
                  </div>
                  <p className="text-xs text-[#E01A23] whitespace-nowrap" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                    {formatPrice(it.price * it.quantity)}
                  </p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E01A23]/10 pt-3 flex items-center justify-between">
              <span className="text-sm text-[#3a3a3a]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>total</span>
              <span className="text-xl text-[#1A1A1A]" data-testid="summary-total" style={{ fontFamily: "'Lazy Dog', cursive" }}>
                {formatPrice(totalPrice)}
              </span>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
